<template>
  <div>
    <!-- HEADER -->
    <page-header class="mt-6 lg:mt-0" :full-width="true">
      <div class="flex flex-wrap -mx-2">
        <div class="w-full px-2 mb-6">
          <h1 slot="header" class="text-grey-darker text-xl font-bold leading-normal">Property Content Management</h1>
        </div>

        <div class="w-full px-2">
          <div v-if="!saving && !loading" class="flex flex-col md:flex-row">
            <button type="button" class="bg-tertiary flex-1 hover:border-tertiary text-white py-2 px-6 mb-1 text-sm border border-grey-lightest hover:border-transparent rounded py-4 mb-2 mx-2" @click="saveProperty()">
              <i class="fas fa-home"></i><br />Save Updates
            </button>

            <a :href="currentProperty.link" target="_blank" class="bg-grey flex-1 text-center hover:border-grey text-black py-2 px-6 mb-1 text-sm border border-grey-lightest hover:border-transparent rounded no-underline py-4 mb-2 mx-2" v-if="!currentProperty.deleted && currentProperty.link">
              <i class="fas fa-link"></i><br />View on Website
            </a>

            <a :href="currentProperty.email_link" target="_blank" class="bg-grey flex-1 text-center hover:border-grey text-black py-2 px-6 mb-1 text-sm border border-grey-lightest hover:border-transparent rounded no-underline py-4 mb-2 mx-2" v-if="!currentProperty.deleted && currentProperty.email_link">
              <i class="fas fa-envelope-open-text"></i><br />Email Details
            </a>

            <button type="button" class="bg-grey flex-1 hover:border-primary text-black py-2 px-6 mb-1 text-sm border border-grey-lightest hover:border-transparent rounded py-4 mb-2 mx-2" @click="duplicateProperty()" v-if="!currentProperty.deleted">
              <i class="fas fa-retweet"></i><br />Duplicate Property
            </button>

            <button type="button" class="bg-grey flex-1 hover:border-primary text-black py-2 px-6 mb-1 text-sm border border-grey-lightest hover:border-transparent rounded py-4 mb-2 mx-2" @click="restoreProperty()" v-if="currentProperty.deleted">
              <i class="fas fa-share"></i><br />Restore Property
            </button>

            <button type="button" class="bg-red flex-1 hover:border-grey text-white py-2 px-6 mb-1 text-sm border border-grey-lightest hover:border-transparent rounded py-4 mb-2 mx-2" @click="showDeleteModal = true" v-if="!currentProperty.deleted">
              <i class="fas fa-trash-alt"></i><br />Delete Property
            </button>

          </div>

          <loader v-if="saving" class="mb-2"></loader>
        </div>
      </div>
    </page-header>

    <!-- NAVIGATION -->
    <loader v-if="loading"></loader>

    <tabs :tabHeaders="tabHeaders" :showIcon="true" :backButton="{url:'/property-management', label:'<< Back to List'}" v-if="!loading">
      <!-- ICON -->
      <div slot="icon">
        <icon :name="'property_cms'" class="p-3" v-if="currentProperty.media.images.length === 0 || currentProperty.media.images[0].url === null"/>
        <div class="icon-img absolute pin" :style="{'background-image': `url(${currentProperty.media.images[0].url.large})`}" v-else></div>
      </div>

      <!-- PROPERTY DETAILS -->
      <div slot="tab0">
        <div class="flex flex-wrap -mx-2">

          <!-- CONTENT -->
          <div class="w-full lg:w-2/3 px-2">
            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Property ID: {{ currentProperty.id }}
              </h1>

              <!-- CHECKBOX OPTIONS -->
              <div class="flex flex-wrap -mx-2 text-xs">

                <!-- ON MARKET -->
                <div class="w-full lg:w-1/2 xl:w-1/3 px-2 mb-2 uppercase">
                  <input type="checkbox" id="on_market" v-model="currentProperty.details.on_market">
                  <label for="on_market">On Market</label>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.on_market')" v-text="errorBag.get('details.on_market')"></small>
                </div>

                <!-- MARKETABLE -->
                <div class="w-full lg:w-1/2 xl:w-1/2 px-2 mb-2 uppercase" v-if="currentProperty.not_visible_to_property_asset === 0 && [7, 8].includes(currentProperty.network_id)">
                  <input type="checkbox" id="unmarketable_flag" v-model="currentProperty.details.unmarketable_flag">
                  <label for="unmarketable_flag">Unavailable for Asset Download</label>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.unmarketable_flag')" v-text="errorBag.get('details.unmarketable_flag')"></small>
                </div>

                <!-- SEND TO PORTALS (EXCLUDE SOUTH AFRICA) -->
                <div class="w-full lg:w-1/2 xl:w-1/3 px-2 mb-2 uppercase" v-if="currentProperty.details.address_country !== 'ZA' && currentProperty.details.on_market">
                  <input type="checkbox" id="send_to_portals" v-model="currentProperty.details.send_to_portals">
                  <label for="send_to_portals">Send to Portals</label>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.send_to_portals')" v-text="errorBag.get('details.send_to_portals')"></small>
                </div>

                <!-- DEVELOPMENT -->
                <div v-if="selectedOffice.send_properties_to_tpj" class="w-full lg:w-1/2 xl:w-1/3 px-2 mb-2 uppercase">
                  <input type="checkbox" id="development" v-model="currentProperty.details.development">
                  <label for="development">Development</label>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.development')" v-text="errorBag.get('details.development')"></small>
                </div>

                <!-- EXCLUSIVE -->
                <div v-if="selectedOffice.send_properties_to_tpj" class="w-full lg:w-1/2 xl:w-1/3 px-2 mb-2 uppercase">
                  <input type="checkbox" id="exclusive" v-model="currentProperty.details.exclusive">
                  <label for="exclusive">Exclusive</label>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.exclusive')" v-text="errorBag.get('details.exclusive')"></small>
                </div>

                <!-- INVESTMENT -->
                <div v-if="selectedOffice.send_properties_to_tpj" class="w-full lg:w-1/2 xl:w-1/3 px-2 mb-2 uppercase">
                  <input type="checkbox" id="investment" v-model="currentProperty.details.investment">
                  <label for="investment">Investment</label>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.investment')" v-text="errorBag.get('details.investment')"></small>
                </div>

                <!-- SEND TO EASY PROPERTY (EASY PROPERTY CUSTOMERS ONLY) -->
                <div class="w-full lg:w-1/2 xl:w-1/3 px-2 mb-2 uppercase" v-if="currentProperty.is_easy_property_customer && currentProperty.details.on_market">
                  <input type="checkbox" id="easy_property_export" v-model="currentProperty.details.easy_property_export">
                  <label for="easy_property_export">Send to easyProperty</label>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.easy_property_export')" v-text="errorBag.get('details.easy_property_export')"></small>
                </div>

                <!-- Discreet Marketing (F&C ONLY) -->
                <div class="w-full lg:w-1/2 xl:w-1/3 px-2 mb-2 uppercase" v-if="[7, 8].includes(currentProperty.network_id)">
                  <input type="checkbox" id="is_discreet" v-model="currentProperty.details.is_discreet">
                  <label for="is_discreet">Discreet Marketing</label>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.is_discreet')" v-text="errorBag.get('details.is_discreet')"></small>
                </div>
                <!-- SEND TO FINANCIAL TIMES -->
                <div class="w-full lg:w-1/2 xl:w-1/3 px-2 mb-2 uppercase" v-if="!currentProperty.details.is_discreet && [7,8].includes(currentProperty.network_id)">
                  <input type="checkbox" id="send_to_financial_times" v-model="currentProperty.details.send_to_financial_times">
                  <label for="send_to_financial_times">Send to Financial Times</label>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.send_to_financial_times')" v-text="errorBag.get('details.send_to_financial_times')"></small>
                </div>

                <!-- SEND TO JAMES EDITION -->
                <div class="w-full lg:w-1/2 xl:w-1/3 px-2 mb-2 uppercase" v-if="currentProperty.details.can_send_to_je && !currentProperty.details.is_discreet && (currentProperty.network_id === 7 || [955, 968, 972, 2419, 2825, 956].includes(currentProperty.client_id))">
                  <input type="checkbox" id="send_to_js_portal" v-model="currentProperty.details.send_to_je_portal">
                  <label for="send_to_js_portal">Send to James Edition Portal</label>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.send_to_je_portal')" v-text="errorBag.get('details.send_to_je_portal')"></small>
                </div>
                <!-- SEND TO PROPER STAR -->
                <div class="w-full lg:w-1/2 xl:w-1/3 px-2 mb-2 uppercase" v-if="!currentProperty.details.is_discreet && currentProperty.network_id === 7">
                  <input type="checkbox" id="send_to_proper_star" v-model="currentProperty.details.send_to_proper_star_portal">
                  <label for="send_to_proper_star">Send to ProperStar Portal</label>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.send_to_proper_star_portal')" v-text="errorBag.get('details.send_to_proper_star_portal')"></small>
                </div>
              </div>
              <div class="flex flex-wrap -mx-2 mt-5">
                <!-- REFERENCE NUMBER -->
                <div class="w-full lg:w-1/2 mb-5 px-2" v-if="!this.currentProperty.inbound_feed">
                  <label for="ref">Reference Number:</label>
                  <input type="text" id="ref" v-model="currentProperty.details.ref" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('details.ref') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.ref')" v-text="errorBag.get('details.ref')"></small>
                </div>

                <!-- FEED ID -->
                <div class="w-full lg:w-1/2 mb-5 px-2" v-if="this.currentProperty.inbound_feed">
                  <label for="ref">{{ this.currentProperty.inbound_feed.name }} Reference Number:</label>
                  <input type="text" id="ref" v-model="this.currentProperty.inbound_feed.ref" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" readonly :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('details.ref') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.ref')" v-text="errorBag.get('details.ref')"></small>
                </div>
              </div>
            </panel>

            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Property Address
              </h1>

              <div class="flex flex-wrap -mx-2">

                <!-- DISPLAY ADDRESS -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="display_address">* Display Address:</label>
                  <input type="text" id="display_address" v-model="currentProperty.details.display_address" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('details.display_address') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.display_address')" v-text="errorBag.get('details.display_address')"></small>
                </div>

                <!-- HOUSE NAME/NUMBER -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="address_name_number">House Name/Number:</label>
                  <input type="text" id="address_name_number" v-model="currentProperty.details.address_name_number" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('details.address_name_number') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.address_name_number')" v-text="errorBag.get('details.address_name_number')"></small>
                </div>

                <!-- ADDRESS LINE 2 -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="address_line2">Street:</label>
                  <input type="text" id="address_line2" v-model="currentProperty.details.address_line2" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('details.address_line2') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.address_line2')" v-text="errorBag.get('details.address_line2')"></small>
                </div>

                <!-- ADDRESS LINE 3 (NON-SOUTH AFRICA) -->
                <div class="w-full lg:w-1/2 mb-5 px-2" v-if="currentProperty.details.address_country !== 'ZA'">
                  <label for="address_line3">Address Line 3:</label>
                  <input type="text" id="address_line3" v-model="currentProperty.details.address_line3" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('details.address_line3') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.address_line3')" v-text="errorBag.get('details.address_line3')"></small>
                </div>

                <!-- ADDRESS LINE 3 (SOUTH AFRICA) -->
                <div class="w-full lg:w-1/2 mb-5 px-2" v-if="currentProperty.details.address_country === 'ZA'">
                  <loader class="float-right" v-if="!currentSuburb.id"></loader>
                  <label for="currentSuburb">Suburb:</label>
                  <select id="currentSuburb" v-model="currentSuburb" @change="suburbChanged()" :disabled="!currentSuburb.id" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('details.address_line3') }">
                    <option v-for="(option, i) in property24.suburbs[currentCity.id]" :key="i" :value="option">{{ option.name }}</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.address_line3')" v-text="errorBag.get('details.address_line3')"></small>
                </div>

                <!-- TOWN/CITY (NON-SOUTH AFRICA) -->
                <div class="w-full lg:w-1/2 mb-5 px-2" v-if="currentProperty.details.address_country !== 'ZA'">
                  <label for="address_town">* {{ currentProperty.details.address_country === 'US' ? 'City' : 'Town/City' }}:</label>
                  <input type="text" id="address_town" v-model="currentProperty.details.address_town" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('details.address_town') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.address_town')" v-text="errorBag.get('details.address_town')"></small>
                </div>

                <!-- TOWN/CITY (SOUTH AFRICA) -->
                <div class="w-full lg:w-1/2 mb-5 px-2" v-if="currentProperty.details.address_country === 'ZA'">
                  <loader class="float-right" v-if="!currentCity.id"></loader>
                  <label for="currentCity">* City:</label>
                  <select id="currentCity" v-model="currentCity" @change="cityChanged()" :disabled="!currentCity.id" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('details.address_town') }">
                    <option v-for="(option, i) in property24.cities[currentProvince.id]" :key="i" :value="option">{{ option.name }}</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.address_town')" v-text="errorBag.get('details.address_town')"></small>
                </div>

                <!-- COUNTY (NON-SOUTH AFRICA) -->
                <div class="w-full lg:w-1/2 mb-5 px-2" v-if="currentProperty.details.address_country !== 'ZA'">
                  <label for="address_line4">* {{ currentProperty.details.address_country === 'US' ? 'State' : 'County' }}:</label>
                  <input type="text" id="address_line4" v-model="currentProperty.details.address_line4" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('details.address_line4') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.address_line4')" v-text="errorBag.get('details.address_line4')"></small>
                </div>

                <!-- COUNTY (SOUTH AFRICA) -->
                <div class="w-full lg:w-1/2 mb-5 px-2" v-if="currentProperty.details.address_country === 'ZA'">
                  <loader class="float-right" v-if="!currentProvince.id"></loader>
                  <label for="currentProvince">* Province:</label>
                  <select id="currentProvince" v-model="currentProvince" @change="provinceChanged()" :disabled="!currentProvince.id" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('details.address_line4') }">
                    <option v-for="(option, i) in property24.provinces" :key="i" :value="option">{{ option.name }}</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.address_line4')" v-text="errorBag.get('details.address_line4')"></small>
                </div>

                <!-- POSTCODE -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="address_postcode">
                    <span v-if="currentProperty.details.address_country === 'GB'">* </span>
                    {{ currentProperty.details.address_country === 'US' ? 'ZIP Code' : 'Postcode' }}:
                  </label>
                  <input type="text" id="address_postcode" v-model="currentProperty.details.address_postcode" @change="formatPostcode" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('details.address_postcode') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.address_postcode')" v-text="errorBag.get('details.address_postcode')"></small>
                </div>

                <!-- COUNTRY -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="address_country">* Country:</label>
                  <select id="address_country" v-model="currentProperty.details.address_country" @change="countryChanged()" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('details.address_country') }">
                    <option v-for="(option, i) in propertyEnums.country" :key="i" :value="option.value">{{ option.label }}</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.address_country')" v-text="errorBag.get('details.address_country')"></small>
                </div>
              </div>
            </panel>

            <!-- PROPERTY SPECS -->
            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Property Specs
              </h1>

              <div class="flex flex-wrap -mx-2">

                <!-- PROPERTY TYPE -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="property_type">Property Type:</label>
                  <select id="property_type" v-model="currentProperty.property_specs.property_type" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.property_type') }">
                    <optgroup v-for="(group, i) in propertyEnums.type" :key="i" :label="group.label">
                      <option v-for="(type, j) in group.options" :key="j" :value="type.value">{{ type.label }}</option>
                    </optgroup>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.property_type')" v-text="errorBag.get('property_specs.property_type')"></small>
                </div>

                <!-- NUMBER OF BEDROOMS -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="bedrooms">* Number of Bedrooms:</label>
                  <input type="number" id="bedrooms" v-model="currentProperty.property_specs.bedrooms" min="0" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.bedrooms') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.bedrooms')" v-text="errorBag.get('property_specs.bedrooms')"></small>
                </div>

                <!-- NUMBER OF BATHROOMS -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="bathrooms">Number of Bathrooms:</label>
                  <input type="number" id="bathrooms" v-model="currentProperty.property_specs.bathrooms" min="0" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.bathrooms') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.bathrooms')" v-text="errorBag.get('property_specs.bathrooms')"></small>
                </div>

                <!-- NUMBER OF RECEPTION ROOMS -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="reception_rooms">Number of Reception Rooms:</label>
                  <input type="number" id="reception_rooms" v-model="currentProperty.property_specs.reception_rooms" min="0" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.reception_rooms') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.reception_rooms')" v-text="errorBag.get('property_specs.reception_rooms')"></small>
                </div>

                <!-- NUMBER OF GARAGES -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="garages">Number of Garages:</label>
                  <input type="number" id="garages" v-model="currentProperty.property_specs.garages" min="0" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.garages') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.garages')" v-text="errorBag.get('property_specs.garages')"></small>
                </div>

                <!-- NEW BUILD -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="new_home">New Build:</label>
                  <select id="new_home" v-model="currentProperty.property_specs.new_home" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.new_home') }">
                    <option :value="false">No</option>
                    <option :value="true">Yes</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.new_home')" v-text="errorBag.get('property_specs.new_home')"></small>
                </div>

                <!-- STUDENT PROPERTY (RENTAL ONLY) -->
                <div class="w-full lg:w-1/2 mb-5 px-2" v-if="currentProperty.is_rental">
                  <label for="student_property">Student Property:</label>
                  <select id="student_property" v-model="currentProperty.property_specs.student_property" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.student_property') }">
                    <option :value="false">No</option>
                    <option :value="true">Yes</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.student_property')" v-text="errorBag.get('property_specs.student_property')"></small>
                </div>

                <!-- FURNISHED (RENTAL ONLY) -->
                <div class="w-full lg:w-1/2 mb-5 px-2" v-if="currentProperty.is_rental">
                  <label for="furnished">Furnished:</label>
                  <select id="furnished" v-model="currentProperty.property_specs.furnished" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.furnished') }">
                    <option :value="null">Not Specified</option>
                    <option v-for="(option, i) in propertyEnums.furnished" :key="i" :value="option.value">{{ option.label }}</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.furnished')" v-text="errorBag.get('property_specs.furnished')"></small>
                </div>
              </div>

              <div class="w-full text-center cursor-pointer mb-5" @click="toggleMoreSpecs()">
                [{{ moreSpecsOpen ? '-' : '+'}}] More Details
              </div>

              <div class="w-full" v-if="moreSpecsOpen">
                <div class="flex flex-wrap -mx-2">

                  <!-- TAGLINE -->
                  <div class="w-full lg:w-1/2 mb-5 px-2">
                    <label for="tagline">Tagline:</label>
                    <input type="text" id="tagline" v-model="currentProperty.property_specs.tag" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.tag') }">
                    <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.tag')" v-text="errorBag.get('property_specs.tag')"></small>
                  </div>

                  <!-- LET TYPE (RENTAL ONLY) -->
                  <div class="w-full lg:w-1/2 mb-5 px-2" v-if="currentProperty.is_rental">
                    <label for="let_type">Let Type:</label>
                    <select id="let_type" v-model="currentProperty.property_specs.let_type" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.let_type') }">
                      <option v-for="(type, i) in propertyEnums.let_type" :key="i" :value="type.value">{{ type.label }}</option>
                    </select>
                    <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.let_type')" v-text="errorBag.get('property_specs.let_type')"></small>
                  </div>

                  <!-- FEATURES POOL -->
                  <div class="w-full lg:w-1/2 mb-5 px-2">
                    <label for="features_pool">Features Pool:</label>
                    <select id="features_pool" v-model="currentProperty.property_specs.features_pool" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.features_pool') }">
                      <option :value="false">No</option>
                      <option :value="true">Yes</option>
                    </select>
                    <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.features_pool')" v-text="errorBag.get('property_specs.features_pool')"></small>
                  </div>

                  <!-- GOLF PROPERTY (F&C ONLY) -->
                  <div class="w-full lg:w-1/2 mb-5 px-2" v-if="currentProperty.network_id === 7 || currentProperty.network_id === 8 || currentProperty.property_specs.golf_property">
                    <label for="golf_property">Golf Property:</label>
                    <select id="golf_property" v-model="currentProperty.property_specs.golf_property" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.golf_property') }">
                      <option :value="false">No</option>
                      <option :value="true">Yes</option>
                    </select>
                    <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.golf_property')" v-text="errorBag.get('property_specs.golf_property')"></small>
                  </div>

                  <!-- YEAR BUILT -->
                  <div class="w-full lg:w-1/2 mb-5 px-2">
                    <label for="year_built">Year Built:</label>
                    <input type="number" id="year_built" v-model="currentProperty.property_specs.year_built" min="0" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.year_built') }">
                    <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.year_built')" v-text="errorBag.get('property_specs.year_built')"></small>
                  </div>

                  <!-- SHARED OWNERSHIP -->
                  <div class="w-full lg:w-1/2 mb-5 px-2">
                    <label for="shared_ownership">Shared Ownership:</label>
                    <select id="shared_ownership" v-model="currentProperty.property_specs.shared_ownership" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.shared_ownership') }">
                      <option :value="null">Select</option>
                      <option :value="false">No</option>
                      <option :value="true">Yes</option>
                    </select>
                    <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.shared_ownership')" v-text="errorBag.get('property_specs.shared_ownership')"></small>
                  </div>

                  <!-- SHARED OWNERSHIP SOLD PERCENTAGE -->
                  <div class="w-full lg:w-1/2 mb-5 px-2" v-if="currentProperty.property_specs.shared_ownership">
                    <label for="shared_ownership_percentage">Shared Ownership Percentage:</label>
                    <input type="number" id="shared_ownership_percentage" v-model="currentProperty.property_specs.shared_ownership_percentage" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.shared_ownership_percentage') }">
                    <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.shared_ownership_percentage')" v-text="errorBag.get('property_specs.shared_ownership_percentage')"></small>
                  </div>

                  <!-- COUNCIL TAX EXEMPT (RENTAL ONLY)-->
                  <div class="w-full lg:w-1/2 mb-5 px-2" v-if="currentProperty.is_rental">
                    <label for="council_tax_exempt">Council Tax Exempt:</label>
                    <select id="council_tax_exempt" v-model="currentProperty.property_specs.council_tax_exempt" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.council_tax_exempt') }">
                      <option :value="null">Select</option>
                      <option :value="false">No</option>
                      <option :value="true">Yes</option>
                    </select>
                    <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.council_tax_exempt')" v-text="errorBag.get('property_specs.council_tax_exempt')"></small>
                  </div>

                  <!-- COUNCIL TAX BAND -->
                  <div class="w-full lg:w-1/2 mb-5 px-2">
                    <label for="council_tax_band">Council Tax Band:</label>
                    <select id="council_tax_band" v-model="currentProperty.property_specs.council_tax_band" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.council_tax_band') }">
                      <option :value="null">Select</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                      <option value="E">E</option>
                      <option value="F">F</option>
                      <option value="G">G</option>
                      <option value="H">H</option>
                      <option value="I">I</option>
                      <option value="TBD">TBD</option>
                    </select>
                    <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.council_tax_band')" v-text="errorBag.get('property_specs.council_tax_band')"></small>
                  </div>
                </div>
              </div>
            </panel>

            <!-- OPEN HOUSE -->
            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Open House
              </h1>

              <div class="flex flex-wrap -mx-2">
                <!-- TIMEZONE -->
                <div class="w-full mb-5 px-2">
                  <label for="timezone">Timezone:</label>
                  <select id="timezone" v-model="currentProperty.open_house.timezone" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('open_house.timezone') }">
                    <option v-for="(option, i) in propertyEnums.timezone" :key="i" :value="option.value">{{ option.label }}</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('open_house.timezone')" v-text="errorBag.get('open_house.timezone')"></small>
                </div>
              </div>

              <div class="flex flex-wrap -mx-2 mb-2 font-bold" v-if="currentProperty.open_house.times.length > 0">
                <div class="w-2/5 px-2">
                  * Start Time
                </div>
                <div class="w-2/5 px-2">
                  * End Time
                </div>
              </div>

              <!-- TIMES -->
              <div class="flex flex-wrap -mx-2 mb-2" v-for="(time, i) in currentProperty.open_house.times" :key="i">
                <div class="w-2/5 px-2">
                  <date-time-picker v-model="time.start" format="d MMMM yyyy HH:mm" value-format="yyyy-MM-dd HH:mm" constraints-format="yyyy-MM-dd HH:mm" :class="'block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm'+(errorBag.get(`open_house.times.${i}.start`) ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')" :minute-step="5" :max-date="time.end">
                    <template slot="date">
                      <button class="bg-primary rounded py-2 px-4 text-white">
                        <i class="fas fa-arrow-left"></i> Set Date
                      </button>
                    </template>
                    <template slot="time">
                      <button class="bg-primary rounded py-2 px-4 text-white">
                        Set Time <i class="fas fa-arrow-right"></i>
                      </button>
                    </template>
                  </date-time-picker>
                </div>
                <div class="w-2/5 px-2">
                  <date-time-picker v-model="time.end" format="d MMMM yyyy HH:mm" value-format="yyyy-MM-dd HH:mm" constraints-format="yyyy-MM-dd HH:mm" :class="'block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm'+(errorBag.get(`open_house.times.${i}.start`) ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')" :minute-step="5" :min-date="time.start">
                    <template slot="date">
                      <button class="bg-primary rounded py-2 px-4 text-white">
                        <i class="fas fa-arrow-left"></i> Set Date
                      </button>
                    </template>
                    <template slot="time">
                      <button class="bg-primary rounded py-2 px-4 text-white">
                        Set Time <i class="fas fa-arrow-right"></i>
                      </button>
                    </template>
                  </date-time-picker>
                </div>
                <div class="w-1/5 px-2 flex flex-wrap content-center">
                  <i class="fas fa-trash-alt cursor-pointer" @click="removeOpenHouse(i)"></i>
                </div>
              </div>

              <div class="flex flex-wrap -mx-2 my-5">
                <div class="w-full mb-5 px-2 text-center">
                  <button class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow" @click="addOpenHouse()">
                    + Add Time
                  </button>
                </div>
              </div>

              <div class="block text-red-dark text-medium" v-for="(time, i) in currentProperty.open_house.times" v-bind:key="`t${i}`">
                <small class="block" v-for="(value, key, index) in time" v-bind:key="index" v-if="errorBag.get(`open_house.times.${i}.${key}`)" v-text="errorBag.get(`open_house.times.${i}.${key}`).replace(':number', i+1)"></small>
              </div>

            </panel>
            <!-- Energy Rating -->
            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Energy Rating
              </h1>
              <div class="flex flex-wrap -mx-2">
                <!-- EER Current Rating -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="eer_current_rating">EER Current Rating:</label>
                  <input type="number" id="eer_current_rating" v-model="currentProperty.property_energy_ratings.eer_current_rating" min="0" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_energy_ratings.eer_current_rating') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_energy_ratings.eer_current_rating')" v-text="errorBag.get('property_energy_ratings.eer_current_rating')"></small>
                </div>
                <!-- EER Potential Rating -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="eer_potential_rating">EER Potential Rating:</label>
                  <input type="number" id="eer_potential_rating" v-model="currentProperty.property_energy_ratings.eer_potential_rating" min="0" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_energy_ratings.eer_potential_rating') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_energy_ratings.eer_potential_rating')" v-text="errorBag.get('property_energy_ratings.eer_potential_rating')"></small>
                </div>
                <!-- EIR Current Rating -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="eir_current_rating">EIR Current Rating:</label>
                  <input type="number" id="eir_current_rating" v-model="currentProperty.property_energy_ratings.eir_current_rating" min="0" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_energy_ratings.eir_current_rating') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_energy_ratings.eir_current_rating')" v-text="errorBag.get('property_energy_ratings.eir_current_rating')"></small>
                </div>
                <!-- EIR Potential Rating -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="eir_potential_rating">EIR Potential Rating:</label>
                  <input type="number" id="eir_potential_rating" v-model="currentProperty.property_energy_ratings.eir_potential_rating" min="0" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_energy_ratings.eir_potential_rating') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_energy_ratings.eir_potential_rating')" v-text="errorBag.get('property_energy_ratings.eir_potential_rating')"></small>
                </div>
                <!-- EPC Exempt -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <input type="checkbox" id="is_epc_exempt" v-model="currentProperty.property_energy_ratings.is_epc_exempt">
                  <label for="is_epc_exempt">EPC Exempt</label>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_energy_ratings.is_epc_exempt')" v-text="errorBag.get('property_energy_ratings.is_epc_exempt')"></small>
                </div>
              </div>
            </panel>
          </div>

          <!-- PRICE & AVAILABILITY -->
          <div class="w-full lg:w-1/3 px-2">
            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                {{ currentProperty.is_rental ? 'Rent' : 'Price' }} &amp; Availability
              </h1>

              <div>

                <!-- PRICE -->
                <div class="w-full mb-5">
                  <label for="price">* {{ currentProperty.is_rental ? 'Rent' : 'Price' }}:</label>
                  <input type="number" id="price" v-model="currentProperty.price_and_availability.price" min="0" :step="currentProperty.is_rental ? 0.01 : 1" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.price') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.price')" v-text="errorBag.get('price_and_availability.price')"></small>
                </div>
                <!-- ALLOW PROPERTIES FOR SALE / TO LET BY INFORMAL TENDER -->
                <div class="w-full mb-5 text-sm" v-if="selectedOffice.show_sale_by_informal_tender_flag">
                  <input type="checkbox" id="is_sale_by_informal_tender" v-model="currentProperty.details.is_sale_by_informal_tender">
                  <label for="is_sale_by_informal_tender" class="ml-2">Property For Sale / <br class="mb-2"><span>To Let by Informal Tender</span></label>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('details.is_sale_by_informal_tender')" v-text="errorBag.get('details.is_sale_by_informal_tender')"></small>
                </div>
                <div class="w-full mb-5 text-sm" v-if="currentProperty.details.is_sale_by_informal_tender">
                  <p class="text-xs mb-2">You must include within the Property Description of this property the details of where the informal tender submissions need to be to, and by what date and time the cut off is for the submissions.</p>
                  <p class="text-xs">The price value will be used to determine where the property should sit in search results should they be filtered or sorted by price. Note that the price will not be visible to the end consumer.</p>
                </div>
                <!-- CURRENCY -->
                <div class="w-full mb-5">
                  <label for="currency">* Currency:</label>
                  <select id="currency" v-model="currentProperty.price_and_availability.currency" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.currency') }">
                    <option v-for="(option, i) in propertyEnums.currency" :key="i" :value="option.value">{{ option.label }}</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.currency')" v-text="errorBag.get('price_and_availability.currency')"></small>
                </div>

                <!-- COMMISSION (GERMANY ONLY) -->
                <div class="w-full mb-5" v-if="currentProperty.details.address_country === 'DE'">
                  <label for="commission">Commission:</label>
                  <input type="text" id="commission" v-model="currentProperty.price_and_availability.commission" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.commission') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.commission')" v-text="errorBag.get('price_and_availability.commission')"></small>
                </div>

                <!-- PRICE QUALIFIER (SALES ONLY) -->
                <div class="w-full mb-5" v-if="!currentProperty.is_rental">
                  <label for="price_qualifier">* Price Qualifier:</label>
                  <select id="price_qualifier" v-model="currentProperty.price_and_availability.price_qualifier" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.price_qualifier') }">
                    <option v-for="(option, i) in propertyEnums.price_qualifier" :key="i" :value="option.value">{{ option.label }}</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.price_qualifier')" v-text="errorBag.get('price_and_availability.price_qualifier')"></small>
                </div>

                <!-- RENT FREQUENCY (RENTAL ONLY) -->
                <div class="w-full mb-5" v-if="currentProperty.is_rental">
                  <label for="rent_frequency">* Rent Frequency:</label>
                  <select id="rent_frequency" v-model="currentProperty.price_and_availability.rent_frequency" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.rent_frequency') }">
                    <option v-for="(option, i) in propertyEnums.rent_frequency" :key="i" :value="option.value">{{ option.label }}</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.rent_frequency')" v-text="errorBag.get('price_and_availability.rent_frequency')"></small>
                </div>

                <!-- DEPOSIT (RENTAL ONLY) -->
                <div class="w-full mb-5" v-if="currentProperty.is_rental">
                  <label for="deposit">Deposit:</label>
                  <input type="number" id="deposit" v-model="currentProperty.price_and_availability.deposit" min="0" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.deposit') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.deposit')" v-text="errorBag.get('price_and_availability.deposit')"></small>
                </div>

                <!-- HIDE PRICE (AUSTRALIA ONLY) -->
                <div class="w-full mb-5" v-if="currentProperty.details.address_country === 'AU'">
                  <label for="hide_price">Hide Price:</label>
                  <select id="hide_price" v-model="currentProperty.price_and_availability.hide_price" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.hide_price') }">
                    <option :value="false">No</option>
                    <option :value="true">Yes</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.hide_price')" v-text="errorBag.get('price_and_availability.hide_price')"></small>
                </div>

                <!-- TEXT OVER PRICE (AUSTRALIA ONLY) -->
                <div class="w-full mb-5" v-if="currentProperty.details.address_country === 'AU'">
                  <label for="text_over_price">Text Over {{ currentProperty.is_rental ? 'Rent' : 'Price' }}:</label>
                  <input type="text" id="text_over_price" v-model="currentProperty.price_and_availability.text_over_price" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.text_over_price') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.text_over_price')" v-text="errorBag.get('price_and_availability.text_over_price')"></small>
                </div>

                <!-- AVAILABILITY -->
                <div class="w-full mb-5">
                  <label for="availability">* Availability:</label>
                  <select id="availability" v-model="currentProperty.price_and_availability.availability" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.availability') }">
                    <option v-for="(option, i) in propertyEnums.status[currentProperty.is_rental ? 'lettings' : 'sales']" :key="i" :value="option.value">{{ option.label }}</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.availability')" v-text="errorBag.get('price_and_availability.availability')"></small>
                </div>

                <!-- SHOW GST (AUSTRALIA ONLY) -->
                <div class="w-full mb-5" v-if="currentProperty.details.address_country === 'AU'">
                  <label for="show_gst">Show +GST:</label>
                  <select id="show_gst" v-model="currentProperty.price_and_availability.show_gst" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.show_gst') }">
                    <option :value="false">No</option>
                    <option :value="true">Yes</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.show_gst')" v-text="errorBag.get('price_and_availability.show_gst')"></small>
                </div>

                <!-- TENURE (SALES ONLY) -->
                <div class="w-full mb-5" v-if="!currentProperty.is_rental">
                  <label for="tenure">* Tenure:</label>
                  <select id="tenure" v-model="currentProperty.price_and_availability.tenure" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.tenure') }">
                    <option v-if="currentProperty.price_and_availability.tenure === null" :value="null">[Not Set]</option>
                    <option v-for="(option, i) in propertyEnums.tenure" :key="i" :value="option.value">{{ option.label }}</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.tenure')" v-text="errorBag.get('price_and_availability.tenure')"></small>
                </div>

                <!-- NEXT AVAILABLE DATE (RENTAL ONLY) -->
                <div class="w-full mb-5" v-if="currentProperty.is_rental">
                  <label for="date_available">Next Available Date:</label>

                  <date-time-picker id="date_available"  v-model="currentProperty.price_and_availability.date_available" format="d MMMM yyyy" value-format="yyyy-MM-dd" :time-picker="false" :class="'block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm mt-2'+(errorBag.get('price_and_availability.date_available') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')"  />
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.date_available')" v-text="errorBag.get('price_and_availability.date_available')"></small>
                </div>

                <!-- CONTRACT MONTHS (RENTAL ONLY) -->
                <div class="w-full mb-5" v-if="currentProperty.is_rental">
                  <label for="contract_months">Contract Months:</label>
                  <input type="number" id="contract_months" v-model="currentProperty.price_and_availability.contract_months" min="0" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.contract_months') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.contract_months')" v-text="errorBag.get('price_and_availability.contract_months')"></small>
                </div>

                <!-- MINIMUM TERM (RENTAL ONLY) -->
                <div class="w-full mb-5" v-if="currentProperty.is_rental">
                  <label for="minimum_term">Minimum Term:</label>
                  <input type="number" id="minimum_term" v-model="currentProperty.price_and_availability.minimum_term" min="0" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.minimum_term') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.minimum_term')" v-text="errorBag.get('price_and_availability.minimum_term')"></small>
                </div>

                <!-- SHARED OWNERSHIP RENT (SHARED OWNERSHIP ONLY) -->
                <div class="w-full mb-5" v-if="currentProperty.is_rental && currentProperty.property_specs.shared_ownership">
                  <label for="shared_ownership_rent">Shared Ownership Rent:</label>
                  <input type="number" id="shared_ownership_rent" v-model="currentProperty.price_and_availability.shared_ownership_rent" min="0" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.shared_ownership_rent') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.shared_ownership_rent')" v-text="errorBag.get('price_and_availability.shared_ownership_rent')"></small>
                </div>

                <!-- SHARED OWNERSHIP RENT FREQUENCY (SHARED OWNERSHIP ONLY) -->
                <div class="w-full mb-5" v-if="currentProperty.is_rental && currentProperty.property_specs.shared_ownership">
                  <label for="shared_ownership_rent_frequency">Shared Ownership Rent Frequency:</label>
                  <input type="number" id="shared_ownership_rent_frequency" v-model="currentProperty.price_and_availability.shared_ownership_rent_frequency" min="0" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.shared_ownership_rent_frequency') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.shared_ownership_rent_frequency')" v-text="errorBag.get('price_and_availability.shared_ownership_rent_frequency')"></small>
                </div>

                <!-- ANNUAL GROUND RENT (RESALE LEASEHOLD ONLY) -->
                <div class="w-full mb-5" v-if="!currentProperty.is_rental">
                  <label for="annual_ground_rent">Annual Ground Rent:</label>
                  <input type="number" id="annual_ground_rent" v-model="currentProperty.price_and_availability.annual_ground_rent" min="0" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.annual_ground_rent') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.annual_ground_rent')" v-text="errorBag.get('price_and_availability.annual_ground_rent')"></small>
                </div>

                <!-- Ground Rent Review Period Years(RESALE LEASEHOLD ONLY) -->
                <div class="w-full mb-5" v-if="!currentProperty.is_rental">
                  <label for="ground_rent_review_period_years">Ground Rent Review Period Years:</label>
                  <input type="number" id="ground_rent_review_period_years" v-model="currentProperty.price_and_availability.ground_rent_review_period_years" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.ground_rent_review_period_years') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.ground_rent_review_period_years')" v-text="errorBag.get('price_and_availability.ground_rent_review_period_years')"></small>
                </div>

                <!-- Ground Rent Percentage Increase(RESALE LEASEHOLD ONLY) -->
                <div class="w-full mb-5" v-if="!currentProperty.is_rental">
                  <label for="ground_rent_percentage_increase">Ground Rent Percentage Increase:</label>
                  <input type="number" id="ground_rent_percentage_increase" v-model="currentProperty.price_and_availability.ground_rent_percentage_increase" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.ground_rent_percentage_increase') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.ground_rent_percentage_increase')" v-text="errorBag.get('price_and_availability.ground_rent_percentage_increase')"></small>
                </div>

                <!-- Annual Service Charge -->
                <div class="w-full mb-5">
                  <label for="annual_service_charge">Annual Service Charge:</label>
                  <input type="number" id="annual_service_charge" v-model="currentProperty.price_and_availability.annual_service_charge" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.annual_service_charge') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.annual_service_charge')" v-text="errorBag.get('price_and_availability.annual_service_charge')"></small>
                </div>

                <!-- Years remaining on lease -->
                <div class="w-full mb-5">
                  <label for="tenure_unexpired_years">Years Remaining on Lease:</label>
                  <input type="number" id="tenure_unexpired_years" v-model="currentProperty.price_and_availability.tenure_unexpired_years" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.tenure_unexpired_years') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.tenure_unexpired_years')" v-text="errorBag.get('price_and_availability.tenure_unexpired_years')"></small>
                </div>

                <!-- Potential Rental Yield-->
                <div class="w-full mb-5">
                  <label for="potential_rental_yield">Potential Rental Yield (%):</label>
                  <input type="number" id="potential_rental_yield" v-model="currentProperty.property_specs.potential_rental_yield" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.potential_rental_yield') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.potential_rental_yield')" v-text="errorBag.get('property_specs.potential_rental_yield')"></small>
                </div>

                <!-- CURRENTLY OCCUPIED -->
                <div class="w-full mb-5">
                  <label for="currently_occupied">Currently Occupied</label>
                  <select id="currently_occupied" v-model="currentProperty.property_specs.currently_occupied" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('property_specs.currently_occupied') }">
                    <option :value="false">No</option>
                    <option :value="true">Yes</option>
                  </select>
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('property_specs.currently_occupied')" v-text="errorBag.get('property_specs.currently_occupied')"></small>
                </div>

                <!-- Domestic Rates (NORTH IRELAND ONLY) -->
                <div class="w-full lg:w-1/2 mb-5 px-2" v-if="currentProperty.details.address_postcode ? currentProperty.details.address_postcode.startsWith('BT') : false">
                  <label for="domestic_rates">Domestic Rates:</label>
                  <input type="number" id="domestic_rates" v-model="currentProperty.price_and_availability.domestic_rates" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('price_and_availability.domestic_rates') }">
                  <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('price_and_availability.domestic_rates')" v-text="errorBag.get('price_and_availability.domestic_rates')"></small>
                </div>
              </div>
            </panel>

            <!-- AGENTS -->
            <panel withHeader v-if="agents.length > 0">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Agent Assignment
              </h1>

              <div class="w-full mb-2 uppercase text-sm" v-for="(agent, i) in agents" :key="i">
                <input type="checkbox" :id="'agent'+i" v-model="currentProperty.agents.assigned" :value="agent.id" @change="agentToggled()">
                <label :for="'agent'+i">{{ agent.name }}</label>
              </div>

              <!-- PRIMARY AGENT -->
              <div class="w-full my-5">
                <label for="primary_agent">Primary Agent:</label>
                <select id="primary_agent" v-model="currentProperty.agents.primary" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
                  <option :value="null">No Assignment</option>
                  <option v-for="(agent, i) in agents" :key="i" :value="agent.id" v-if="currentProperty.agents.assigned.indexOf(agent.id) >= 0">{{ agent.name }}</option>
                </select>
              </div>

              <div class="w-full mb-2 text-xs">
                Agent information will only be displayed on websites configured to use the 5th, 8th and 9th property details layouts.
              </div>
            </panel>

            <!-- Live feeds -->
            <panel withHeader class="mt-5" v-if="currentProperty.live_outbound_feeds.feeds.length > 0">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Live Feeds
              </h1>

              <p class="text-xs mb-6 -mt-5">
                View this property on your live outbound property portals.
              </p>

              <div class="flex flex-wrap -mx-2 items-center">
                <div class="w-1/2 mb-5 px-2 text-center" v-for="(feed, i) in currentProperty.live_outbound_feeds.feeds" :key="i" v-if="feed.url">
                  <a :href="feed.url" target="_blank">
                    <img :src="`/feed_provider_logos/${feed.uid}.png`" class="mw-full mh-30" />
                  </a>
                </div>
              </div>

              <button type="button" class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow" @click="feedLogVisible = true">
                View Log
              </button>

              <modal v-if="feedLogVisible" @leave="feedLogVisible = false">
                <div class="text-left">
                  <h2 class="mb-6">Live Feed Log</h2>

                  <ul class="list-none">
                    <li v-for="(log, i) in currentProperty.live_outbound_feeds.log" :key="i">
                      <i class="fas fa-fw" :class="{'fa-check-circle text-green': log.level === 'success', 'fa-times-circle text-red-dark': log.level !== 'success'}"></i>
                      {{ log.body }}<br />
                      <small class="text-grey">{{ log.created_at }}</small>

                      <ul v-if="log.data.length" class="mt-1 text-sm">
                        <li v-for="(info, i) in log.data" :key="i">
                          {{ info }}
                        </li>
                      </ul>

                      <ul class="mt-1 text-sm" v-else-if="log.data.url">
                        <li><a :href="log.data.url" target="_blank" class="text-tertiary">View Online</a></li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </modal>
            </panel>
          </div>
        </div>
      </div>

      <!-- PROPERTY DESCRIPTION -->
      <div slot="tab1">
        <div class="flex flex-wrap -mx-2">

          <!-- CONTENT -->
          <div class="w-full lg:w-2/3 px-2">
            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                * Main Summary
              </h1>

              <div class="flex flex-wrap -mx-2">

                <!-- DESCRIPTION TITLE -->
                <div class="w-full mb-5 px-2">
                  <label for="description_title">Title:</label>
                  <div class="text-right mt-2 text-grey text-sm">
                    <span :class="currentProperty.description[currentLanguage].description_title ? currentProperty.description[currentLanguage].description_title.length  > 120 ? 'text-red' : '' : ''">{{ currentProperty.description[currentLanguage].description_title ? currentProperty.description[currentLanguage].description_title.length : 0 | number }}</span> / 120
                  </div>
                  <input type="text" id="description_title" v-model="currentProperty.description[currentLanguage].description_title" :placeholder="default_description_title" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get(`description.${currentLanguage}.title`) }">
                  <div class="text-red-dark text-medium mb-3" v-for="(langData, lang) in currentProperty.description" v-bind:key="lang" v-if="errorBag.get(`description.${lang}.title`)">
                    <small class="block text-red-dark text-medium mt-2" v-text="errorBag.get(`description.${lang}.title`).replace(':lang', languageLabel(lang))"></small>
                  </div>
                </div>

                <!-- MAIN SUMMARY -->
                <div class="w-full mb-5 px-2">
                  <editor @update="currentProperty.description[currentLanguage].summary = $event" :content="currentProperty.description[currentLanguage].summary" :options="['bold', 'italic', 'underline', 'list-item', 'bullet-list', 'ordered-list', 'history']" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 text-sm property-cms-editor" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get(`description.${currentLanguage}.summary`) }"/>
                  <div class="text-right mt-2 text-grey text-sm">
                    {{ summaryLength | number }} / 4,000
                  </div>
                  <div class="text-red-dark text-medium mb-3" v-for="(langData, lang) in currentProperty.description" v-bind:key="lang" v-if="errorBag.get(`description.${lang}.summary`)">
                    <small class="block text-red-dark text-medium mt-2" v-text="errorBag.get(`description.${lang}.summary`).replace(':lang', languageLabel(lang))"></small>
                  </div>
                </div>

              </div>
            </panel>

            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Full Description
              </h1>

              <div class="flex flex-wrap -mx-2">

                <!-- FULL DESCRIPTION -->
                <div class="w-full mb-5 px-2">
                  <editor @update="currentProperty.description[currentLanguage].description = $event" :content="currentProperty.description[currentLanguage].description" :options="['bold', 'italic', 'underline', 'list-item', 'bullet-list', 'ordered-list', 'history']" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 text-sm property-cms-editor" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get(`description.${currentLanguage}.description`) }"/>
                  <div class="text-right mt-2 text-grey text-sm" :class="{'text-red-dark': currentProperty.description[currentLanguage].description.length > 32000 }">
                    {{ descriptionLength | number }} / 32,000
                  </div>
                  <div class="text-red-dark text-medium mb-3" v-for="(langData, lang) in currentProperty.description" v-bind:key="lang" v-if="errorBag.get(`description.${lang}.description`)">
                    <small class="block text-red-dark text-medium mt-2" v-text="errorBag.get(`description.${lang}.description`).replace(':lang', languageLabel(lang))"></small>
                  </div>
                </div>

              </div>
            </panel>

            <!-- ROOMS -->
            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Rooms
              </h1>

              <div class="flex flex-wrap -mx-2" v-if="currentRoom !== null">
                <div class="w-full px-2 text-right">
                  <i class="fas fa-trash-alt cursor-pointer float-right" @click="removeRoom()"></i>
                </div>

                <!-- NAME -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="room_name">* Name:</label>
                  <input type="text" id="room_name" v-model="currentProperty.description[currentLanguage].rooms[currentRoom].name" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get(`description.${currentLanguage}.rooms.${currentRoom}.name`) }">
                </div>

                <!-- DIMENSIONS -->
                <div class="w-full lg:w-1/2 mb-5 px-2">
                  <label for="room_dimension_text">Dimensions:</label>
                  <input type="text" id="room_dimension_text" v-model="currentProperty.description[currentLanguage].rooms[currentRoom].dimension_text" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get(`description.${currentLanguage}.rooms.${currentRoom}.dimension_text`) }">
                </div>

                <!-- DESCRIPTION -->
                <div class="w-full px-2">
                  <label for="room_description">* Description:</label>
                  <textarea id="room_description" v-model="currentProperty.description[currentLanguage].rooms[currentRoom].description" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-48 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get(`description.${currentLanguage}.rooms.${currentRoom}.description`) }"></textarea>
                  <div class="text-right mt-2 text-grey text-sm">
                    {{ currentProperty.description[currentLanguage].rooms[currentRoom].description.length }} / 1,000
                  </div>
                </div>
              </div>

              <div class="flex flex-wrap -mx-2">
                <!-- ROOM NAVIGATION -->
                <div class="w-full px-2 text-center mb-2" v-if="currentProperty.description[currentLanguage].rooms.length > 0">
                  Room {{ currentRoom+1 }} / {{ currentProperty.description[currentLanguage].rooms.length }}
                </div>
                <div class="w-full px-2 text-center">
                  <button v-if="currentProperty.description[currentLanguage].rooms.length > 1" class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white p-2 text-sm border border-tertiary hover:border-transparent rounded shadow" @click="previousRoom()">
                    <i class="fas fa-chevron-left"></i>
                  </button>

                  <button class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 mx-2 text-sm border border-tertiary hover:border-transparent rounded shadow" @click="addRoom()">
                    + Add Room
                  </button>

                  <button v-if="currentProperty.description[currentLanguage].rooms.length > 1" class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white p-2 text-sm border border-tertiary hover:border-transparent rounded shadow" @click="nextRoom()">
                    <i class="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>

              <!-- VALIDATION ERRORS -->
              <div class="text-red-dark text-medium" v-for="(langData, lang, langIndex) in currentProperty.description" v-bind:key="langIndex">
                <div v-for="(roomData, roomIndex) in langData.rooms" v-bind:key="roomIndex">
                  <small class="block" v-for="(value, key, index) in roomData" v-bind:key="index" v-if="errorBag.get(`description.${lang}.rooms.${roomIndex}.${key}`)" v-text="errorBag.get(`description.${lang}.rooms.${roomIndex}.${key}`).replace(':lang', languageLabel(lang)).replace(':number', roomIndex+1)"></small>
                </div>
              </div>
            </panel>
          </div>

          <!-- SIDEBAR -->
          <div class="w-full lg:w-1/3 px-2">
            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Language
              </h1>

              <div>
                <select id="language" v-model="currentLanguage" v-on:change="changeLanguage()" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm mb-2">
                  <option v-for="(option, i) in propertyEnums.language" :key="i" :value="option.value">{{ option.label }}</option>
                </select>

                <p class="text-sm">You are editing the {{ languageLabel(this.currentLanguage) }} version of the property description.</p>
              </div>
            </panel>

            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Bullet Points
              </h1>

              <div>
                <!-- VALIDATION ERRORS -->
                <div class="text-red-dark text-medium mb-3" v-for="(langData, lang) in currentProperty.description" v-bind:key="lang">
                  <small class="block" v-for="(feature, i) in langData.features" v-bind:key="i" v-if="errorBag.get(`description.${lang}.features.${i}`)" v-text="errorBag.get(`description.${lang}.features.${i}`).replace(':lang', languageLabel(lang)).replace(':number', i+1)"></small>
                </div>

                <!-- FEATURES -->
                <div class="w-full mb-5" v-for="(option, i) in 10" :key="i">
                  <label :for="'feature'+i">Feature {{ i+1 }}:</label>
                  <input type="text" :id="'feature'+i" v-model="currentProperty.description[currentLanguage].features[i]" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get(`description.${currentLanguage}.features.${i}`) }">
                </div>
              </div>
            </panel>
          </div>
        </div>
      </div>

      <!-- PROPERTY MEDIA -->
      <div slot="tab2">
        <div class="flex flex-wrap -mx-2">

          <!-- IMAGES -->
          <div class="w-full lg:w-2/3 px-2">
            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Property Images
              </h1>

              <div class="flex flex-wrap -mx-2">
                <cms-gallery :cards="currentProperty.media.images" :type="'Image'" :media-type="0" :accept="'image/*'" :feed-provider="currentProperty.inbound_feed" :cardClass="'w-full md:w-1/2 lg:w-1/3 xl:1/3 p-2 mb-5'" />
              </div>
            </panel>

            <div class="w-full">
              <div class="flex flex-wrap items-stretch -mx-2">

                <!-- FLOORPLANS -->
                <div class="w-full lg:w-1/2 px-2 flex-1 flex items-stretch">
                  <panel withHeader class="mb-5 flex-1">
                    <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                      Floorplans
                    </h1>

                    <div class="flex flex-wrap -mx-2">
                      <cms-gallery :cards="currentProperty.media.floorplans" :type="'Floorplan'" :media-type="1" :accept="'image/*,application/pdf'" :feed-provider="currentProperty.inbound_feed" :cardClass="'lg:w-1/2 p-2 mb-5'" />
                    </div>
                  </panel>
                </div>

                <!-- VIDEO -->
                <div class="w-full lg:w-1/2 px-2 flex-1 flex items-stretch">
                  <panel withHeader class="mb-5 flex-1">
                    <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                      Video
                    </h1>

                    <div class="flex flex-wrap -mx-2">
                      <cms-video :media="currentProperty.media.videos"></cms-video>
                    </div>
                  </panel>
                </div>
              </div>
            </div>

            <div class="w-full">
              <div class="flex flex-wrap items-stretch -mx-2">

                <!-- EPCs -->
                <div class="w-full lg:w-1/2 px-2 flex-1 flex items-stretch">
                  <panel withHeader class="w-full mb-5">
                    <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                      EPCs
                    </h1>

                    <div class="flex flex-wrap -mx-2">
                      <cms-gallery :cards="currentProperty.media.epcs" :type="'EPC'" :media-type="2" :accept="'image/*,application/pdf'" :feed-provider="currentProperty.inbound_feed" :cardClass="'lg:w-1/2 p-2 mb-5'" />
                    </div>
                  </panel>
                </div>

                <!-- DOCUMENTS -->
                <div class="w-full lg:w-1/2 px-2 flex-1 flex items-stretch">
                  <panel withHeader class="w-full mb-5">
                    <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                      Documents
                    </h1>

                    <div class="flex flex-wrap -mx-2">
                      <cms-gallery :cards="currentProperty.media.documents" :type="'Document'" :media-type="3" :accept="'image/*,application/pdf'" :feed-provider="currentProperty.inbound_feed" :cardClass="'lg:w-1/2 p-2 mb-5'" />
                    </div>
                  </panel>
                </div>
              </div>
            </div>

            <!-- VIRTUAL TOURS -->
            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Virtual Tours
              </h1>

              <div class="flex flex-wrap -mx-2">
                <div class="w-full px-2" v-if="currentProperty.media.virtual_tours.length > 0">
                  <div class="flex flex-wrap mb-2 -mx-2">
                    <div class="w-2/5 px-2 font-bold">
                      Caption
                    </div>
                    <div class="w-3/5 px-2 font-bold">
                      URL
                    </div>
                  </div>
                </div>

                <div class="w-full px-2" v-for="(option, i) in currentProperty.media.virtual_tours" :key="i">
                  <div class="flex flex-wrap mb-2 -mx-2">
                    <div class="w-2/5 px-2">
                      <input type="text" v-model="currentProperty.media.virtual_tours[i].caption" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm">
                    </div>
                    <div class="w-2/5 px-2">
                      <input type="url" v-model="currentProperty.media.virtual_tours[i].url" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm">
                    </div>
                    <div class="w-1/5 px-2 flex flex-wrap content-center">
                      <i class="fas fa-trash-alt" @click="removeVirtualTour(currentProperty.media.virtual_tours[i])"></i>
                    </div>
                  </div>
                </div>

                <div class="w-full my-5 px-2 text-center">
                  <button class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow" @click="addVirtualTour()">
                    + Add Virtual Tour
                  </button>
                </div>
              </div>
            </panel>
          </div>

          <!-- LOCATION -->
          <div class="w-full lg:w-1/3 px-2">
            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Property Location
              </h1>

              <p class="text-sm mb-5">Manually enter the location of the property below by changing the lat/long figures or by dragging the pin around the map. The 'Set Automatically' button will find the location of the property for you based on the address provided. <strong>You must provide this information for the property to appear through a radial search on your website.</strong></p>

              <button class="bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white p-2 mb-5 text-sm border border-tertiary hover:border-transparent rounded shadow" @click="mapGeocode()" v-if="!geocoding">
                <i class="fas fa-map-marker-alt"></i> Set Automatically
              </button>
              <loader v-if="geocoding" class="my-5"></loader>

              <!-- LATITUDE -->
              <div class="w-full mb-5">
                <label for="latitude">* Latitude:</label>
                <input type="text" id="latitude" v-model="currentProperty.location.coords.lat" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
              </div>

              <!-- LONGITUDE -->
              <div class="w-full mb-5">
                <label for="longitude">* Longitude:</label>
                <input type="text" id="longitude" v-model="currentProperty.location.coords.lng" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
              </div>

              <!-- FLOOR SIZE -->
              <div class="flex flex-wrap -mx-2">
                <!-- VALUE -->
                <div class="w-2/3 mb-5 px-2">
                  <label for="floor_size">Floor Size:</label>
                  <input type="number" id="floor_size" v-model="currentProperty.location.floor_size" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('location.floor_size') }">
                  <small class="block text-red-dark text-medium mt-2" v-text="errorBag.get('location.floor_size')"></small>
                </div>

                <!-- UNIT -->
                <div class="w-1/3 mb-5 px-2">
                  <label for="floor_size_units">Units:</label>
                  <select id="floor_size_units" v-model="currentProperty.location.floor_size_unit" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
                    <option v-for="(option, i) in propertyEnums.dimension_unit.large" :key="i" :value="option.value">{{ option.label }}</option>
                  </select>
                </div>
              </div>

              <!-- LAND SIZE -->
              <div class="flex flex-wrap -mx-2">
                <!-- VALUE -->
                <div class="w-2/3 mb-5 px-2">
                  <label for="land_size">Land Size:</label>
                  <input type="number" id="land_size" v-model="currentProperty.location.land_size" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('location.land_size') }">
                  <small class="block text-red-dark text-medium mt-2" v-text="errorBag.get('location.land_size')"></small>
                </div>

                <!-- UNIT -->
                <div class="w-1/3 mb-5 px-2">
                  <label for="land_size_units">Units:</label>
                  <select id="land_size_units" v-model="currentProperty.location.land_size_unit" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
                    <option v-for="(option, i) in propertyEnums.dimension_unit.large" :key="i" :value="option.value">{{ option.label }}</option>
                  </select>
                </div>
              </div>

              <!-- GOOGLE MAPS -->
              <GmapMap
                :center="currentProperty.location.coords"
                :zoom="15"
                :options="{disableDefaultUI: true, zoomControl: true}"
                map-type-id="roadmap"
                class="w-full h-48 mb-5"
              >
                <GmapMarker
                  :position="currentProperty.location.coords"
                  :clickable="true"
                  :draggable="true"
                  @dragend="mapPinMoved"
                />
              </GmapMap>
              <gmap-street-view-panorama
                :position="currentProperty.location.coords"
                :options="{addressControl: false, fullscreenControl: false, linksControl: false}"
                :zoom="currentProperty.location.streetview.zoom"
                :pov="currentProperty.location.streetview.pov"
                @pov_changed="mapPovChanged"
                style="width: 100%; height: 150px"
              >
              </gmap-street-view-panorama>
            </panel>
          </div>
        </div>
      </div>
      <!-- PROPERTY LIFESTYLE TAGS -->
      <div slot="tab3" v-if="currentProperty.network_id === 7 || currentProperty.network_id === 8 ">
        <div class="flex flex-wrap -mx-2">
          <!-- CONTENT -->
          <div class="w-full lg:w-2/3 px-2">
            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Property Lifestyle Tags
              </h1>
              <!-- CHECKBOX OPTIONS -->
              <div class="flex flex-wrap -mx-2">
                <!-- Tags -->
                <div class="w-full mb-5 px-2" v-for="tag in currentProperty.tags" :key="tag">
                  <h4 class="text font-semibold mb-1">{{tag.name}}</h4>
                  <p class="mb-1">Property Types: {{tag.property_types}}</p>
                  <p class="mb-1">Phrases: {{tag.features}}, <span v-if="tag.features!='None'">{{tag.full_search}}</span></p>
                  <p v-if="tag.name === 'City Living'">Other: Property is located in an Inner London postcode, defined as one of the following postcode areas: E, EC, N, NW, SE, SW, W, WC</p>
                </div>
              </div>
            </panel>
          </div>
        </div>
      </div>
    </tabs>

    <p v-if="!loading" class="text-xs mb-6">
      * Valid input is required
    </p>
    <deletion-modal
      v-if="showDeleteModal"
      @confirmed="deleteProperty"
      @leave="showDeleteModal = false"
      @dismissed="showDeleteModal = false">
      <p class="text-center">Are you sure you wish to delete the property?</p>
    </deletion-modal>
    <modal
      v-if="leaveModal"
      :scrollY="false"
      @dismissed="leaveModal = false"
      @leave="leaveModal = false"
      :showCloseBtn="true"
    >
      <div class="flex h-full flex-col items-center justify-center">
        <div class="p-2 mb-4">
          <p class="text-center">Are you sure you wish to leave before saving your changes?</p>
        </div>
        <div class="flex items-center">
          <button  @click="buttonPress = true, $router.push({path: destination.path})" class="bg-tertiary border border-tertiary text-white rounded px-6 py-3 text-sm font-medium align-bottom m-2">
            Yes, leave.
          </button>
          <button @click="leaveModal = false" class="bg-white border border-tertiary text-tertiary rounded px-4 py-3 text-sm font-medium align-bottom m-2">
            No, go back.
          </button>
        </div>
      </div>
    </modal>
  </div>

</template>

<script>
import { mapState, mapActions } from 'vuex'
import ErrorBag from '@/ErrorBag'
import api from '@/http'
import { EventBus } from '@/EventBus'
import _ from 'lodash'
import moment from 'moment'
import DeletionModal from '@/components/Modals/DeletionModal'
import DateTimePicker from 'vue-vanilla-datetime-picker'

export default {
  name: 'PropertyContentManagement',
  components: { DeletionModal, DateTimePicker },
  beforeRouteLeave (to, from, next) {
    if (!this.buttonPress && !_.isEqual(this.initialLoadedProperty, JSON.parse(JSON.stringify(this.currentProperty)))) {
      this.leaveModal = true
      this.destination = to
    } else {
      next()
    }
  },
  data () {
    return {
      initialLoadedProperty: null,
      propertyValueChanged: false,
      destination: null,
      buttonPress: false,
      answer: false,
      leaveModal: false,
      showDeleteModal: false,
      media: [{}],
      currentLanguage: 'en',
      currentRoom: null,
      currentProvince: null,
      currentCity: null,
      currentSuburb: null,
      moreSpecsOpen: false,
      agents: [],
      errorBag: new ErrorBag(),
      loading: true,
      saving: false,
      geocoding: false,
      summaryLength: 0,
      descriptionLength: 0,
      interval: null,
      errorCounts: {
        details: 0,
        description: 0,
        media: 0
      },
      initialOfficeId: null,
      feedLogVisible: false
    }
  },
  computed: {
    ...mapState(['currentProperty', 'propertyEnums', 'property24', 'selectedOffice']),
    default_description_title: function () {
      if (!this.currentProperty || !this.propertyEnums) {
        return ''
      }

      let output = []

      // Bedroom Count
      if (this.currentProperty.property_specs.bedrooms > 0) {
        output.push(this.currentProperty.property_specs.bedrooms + ' Bedroom')
      }

      // Property Type
      for (let i = 0; i < this.propertyEnums.type.length; i++) {
        for (let j = 0; j < this.propertyEnums.type[i].options.length; j++) {
          if (this.propertyEnums.type[i].options[j].value === this.currentProperty.property_specs.property_type) {
            output.push(this.propertyEnums.type[i].options[j].label)
          }
        }
      }

      // Department
      output.push(this.currentProperty.is_rental ? 'For Rent in' : 'For Sale in')

      // Town
      output.push(this.currentProperty.details.address_line3 ? this.currentProperty.details.address_line3 : this.currentProperty.details.address_town)

      return output.join(' ')
    },
    tabHeaders: function () {
      var tab = [
        {
          label: 'Property Details',
          badge: this.errorCounts.details
        },
        {
          label: 'Property Description',
          badge: this.errorCounts.description
        },
        {
          label: 'Property Media',
          badge: this.errorCounts.media
        }
      ]
      if (this.currentProperty.network_id === 7 || this.currentProperty.network_id === 8) {
        tab.push({ label: 'Property Lifestyle Tags' })
      }
      return tab
    }
  },
  watch: {
    selectedOffice: {
      handler: function () {
        console.log('change office', this.initialOfficeId, this.selectedOffice)
        if (this.initialOfficeId === null) {
          this.initialOfficeId = this.selectedOffice.id
          return
        }
        this.$router.replace(`/property-management`)
      },
      deep: true
    },
    currentProperty: function (newVal, oldVal) {
      if (newVal.id !== oldVal.id) {
        this.loadData()
      }
    }
  },
  methods: {
    ...mapActions(['displayToastNotification', 'setPageLoading', 'updateProperty', 'fetchProperty', 'fetchPropertyEnums', 'fetchPropertyAgents', 'fetchProperty24Provinces', 'fetchProperty24Cities', 'fetchProperty24Suburbs']),
    languageLabel: function (langCode) {
      for (let i = 0; i < this.propertyEnums.language.length; i++) {
        if (this.propertyEnums.language[i].value === langCode) {
          return this.propertyEnums.language[i].label
        }
      }
    },
    addRoom () {
      this.currentProperty.description[this.currentLanguage].rooms.push({
        name: '',
        dimension_text: '',
        description: ''
      })
      this.currentRoom = this.currentProperty.description[this.currentLanguage].rooms.length - 1
    },
    removeRoom () {
      this.currentProperty.description[this.currentLanguage].rooms.splice(this.currentRoom, 1)

      if (this.currentProperty.description[this.currentLanguage].rooms.length === 0) {
        this.currentRoom = null
      } else {
        this.previousRoom()
      }
    },
    previousRoom () {
      let nextRoom = this.currentRoom - 1
      if (nextRoom < 0) {
        nextRoom = this.currentProperty.description[this.currentLanguage].rooms.length - 1
      }
      this.currentRoom = nextRoom
    },
    nextRoom () {
      let nextRoom = this.currentRoom + 1
      if (nextRoom >= this.currentProperty.description[this.currentLanguage].rooms.length) {
        nextRoom = 0
      }
      this.currentRoom = nextRoom
    },

    addOpenHouse () {
      this.currentProperty.open_house.times.push({
        start: '',
        end: ''
      })
    },
    removeOpenHouse (i) {
      this.currentProperty.open_house.times.splice(i, 1)
    },
    agentToggled () {
      for (let i = 0; i < this.agents.length; i++) {
        if (this.agents[i].id === this.currentProperty.agents.primary && this.currentProperty.agents.assigned.indexOf(this.agents[i].id) >= 0) {
          return
        }
      }

      this.currentProperty.agents.primary = null
    },

    // START - SOUTH AFRICA
    countryChanged () {
      if (this.currentProperty.details.address_country === 'ZA') {
        this.currentProvince = {}
        this.currentCity = {}
        this.currentSuburb = {}
        this.fetchProperty24Provinces('ZA').then(() => {
          this.currentProvince = this.property24.provinces[0]
          this.currentProperty.details.address_line4 = this.currentProvince.name
          this.provinceChanged()
        }, () => {
          this.displayError('Error', 'Failed to retrieve province listing from Property24')
        })
      }
    },
    provinceChanged () {
      this.currentCity = {}
      this.currentSuburb = {}
      this.currentProperty.details.address_line4 = this.currentProvince.name
      this.fetchProperty24Cities(this.currentProvince.id).then(() => {
        this.currentCity = this.property24.cities[this.currentProvince.id][0]
        this.currentProperty.details.address_town = this.currentCity.name
        this.cityChanged()
      }, () => {
        this.displayError('Error', 'Failed to retrieve city listing from Property24')
      })
    },
    cityChanged () {
      this.currentSuburb = {}
      this.currentProperty.details.address_town = this.currentCity.name
      this.fetchProperty24Suburbs(this.currentCity.id).then(() => {
        this.currentSuburb = this.property24.suburbs[this.currentCity.id][0]
        this.currentProperty.details.address_line3 = this.currentSuburb.name
      }, () => {
        this.displayError('Error', 'Failed to retrieve suburb listing from Property24')
      })
    },
    suburbChanged () {
      this.currentProperty.details.address_line3 = this.currentSuburb.name
    },
    // END - SOUTH AFRICA
    isEmpty (value) {
      if (value) {
        value = value.replace(/<\/?p[^>]*>/g, '')
        value = value.trim()
      }
      return !value
    },
    removeEmptyLanguages () {
      for (let i = 0; i < this.propertyEnums.language.length; i++) {
        let code = this.propertyEnums.language[i].value

        // Do not remove English
        if (code === 'en' || !this.currentProperty.description[code]) {
          continue
        }

        // Remove from array if there's no value
        if (this.isEmpty(this.currentProperty.description[code].description_title) && this.isEmpty(this.currentProperty.description[code].summary) && this.isEmpty(this.currentProperty.description[code].features[0])) {
          delete this.currentProperty.description[code]
        }
      }
    },
    changeLanguage () {
      // Need to remove languages that have no value
      for (let i = 0; i < this.propertyEnums.language.length; i++) {
        let code = this.propertyEnums.language[i].value

        // Do not remove English
        if (code === 'en' || !this.currentProperty.description[code]) {
          continue
        }

        // Remove from array if there's no value
        if (this.isEmpty(this.currentProperty.description[code].description_title) && this.isEmpty(this.currentProperty.description[code].summary) && this.isEmpty(this.currentProperty.description[code].features[0])) {
          delete this.currentProperty.description[code]
        }
      }

      // Language needs initialising
      if (!this.currentProperty.description[this.currentLanguage]) {
        this.currentProperty.description[this.currentLanguage] = {
          description_title: '',
          summary: '',
          description: '',
          features: ['', '', '', '', '', '', '', '', '', ''],
          rooms: []
        }
      }

      // Current room needs resetting
      this.currentRoom = this.currentProperty.description[this.currentLanguage].rooms.length > 0 ? 0 : null

      // Editors need their content changing
      EventBus.$emit('editor:refresh')
    },
    toggleMoreSpecs () {
      this.moreSpecsOpen = !this.moreSpecsOpen
    },

    addVirtualTour () {
      this.currentProperty.media.virtual_tours.push({
        caption: 'Virtual Tour',
        url: '',
        cms_created: true
      })
    },
    removeVirtualTour (vt) {
      let index = this.currentProperty.media.virtual_tours.indexOf(vt)
      this.currentProperty.media.virtual_tours.splice(index, 1)
    },

    mapGeocode () {
      this.geocoding = true
      let location = [
        [this.currentProperty.details.address_name_number, this.currentProperty.details.address_line2].join(' '),
        this.currentProperty.details.address_line3,
        this.currentProperty.details.address_town,
        this.currentProperty.details.address_line4,
        this.currentProperty.details.address_postcode
      ].filter(Boolean).join(', ')

      this.$store.dispatch('geocode', location).then((response) => {
        this.currentProperty.location.coords.lat = response.lat
        this.currentProperty.location.coords.lng = response.lon
        this.geocoding = false
      }, () => {
        this.displayError('Error', 'Failed to geocode the location of this address')
        this.geocoding = false
      })
    },
    mapPinMoved (pin) {
      this.currentProperty.location.coords.lat = pin.latLng.lat()
      this.currentProperty.location.coords.lng = pin.latLng.lng()
    },
    mapPovChanged (pov) {
      this.currentProperty.location.streetview.pov.heading = pov.heading
      this.currentProperty.location.streetview.pov.pitch = pov.pitch
    },

    formatPostcode () {
      this.currentProperty.details.address_postcode = this.currentProperty.details.address_postcode.toUpperCase()

      if (this.currentProperty.details.address_country !== 'GB') {
        return
      }

      let matches = /^([0-9A-Z]+)([0-9][A-Z]{2})$/gi.exec(this.currentProperty.details.address_postcode)
      if (matches) {
        this.currentProperty.details.address_postcode = `${matches[1]} ${matches[2]}`
      }
    },

    addMedia () {
      this.media.push({
        type: '',
        link: ''
      })
    },
    initForm () {
      this.initVideo()

      // Default timezone
      if (this.currentProperty.open_house.times.length === 0) {
        this.currentProperty.open_house.timezone = 'Europe/London'
      }

      // Inbound feed warning
      if (this.currentProperty.inbound_feed) {
        this.displayToastNotification({
          type: 'warning',
          icon: 'fa-exclamation-triangle',
          title: 'Inbound Feed',
          info: `This property was received from ${this.currentProperty.inbound_feed.name}. Any changes may get overwritten.`
        })
      }

      // Desctiption counts. Computed properties do not work for this... :(
      clearInterval(this.interval)
      this.interval = setInterval(() => {
        this.summaryLength = this.currentProperty.description[this.currentLanguage].summary.length
        this.descriptionLength = this.currentProperty.description[this.currentLanguage].description.length
      }, 500)

      // Display the first room if defined
      this.currentRoom = this.currentProperty.description[this.currentLanguage].rooms.length > 0 ? 0 : null

      this.loading = false
      this.setPageLoading(false)
      this.initialLoadedProperty = JSON.parse(JSON.stringify(this.currentProperty))
    },
    initVideo () {
      // Placeholder video media
      if (this.currentProperty.media.videos.length === 0) {
        this.currentProperty.media.videos.push({
          url: '',
          caption: 'Video',
          cms_created: true
        })
      }
    },
    displayError (title, message) {
      this.displayToastNotification({
        type: 'danger',
        icon: 'fa-times-circle',
        title: title,
        info: message
      })
    },

    formatOpenHouse (time, key) {
      time[key] = moment(time[key]).format('YYYY-MM-DD HH:mm:ss')
    },
    checkIfCurrentFormIsEmpty () {
      if (this.isEmpty(this.currentProperty.description[this.currentLanguage].description) && this.isEmpty(this.currentProperty.description[this.currentLanguage].summary)) {
        return true
      }
      return false
    },
    saveProperty () {
      if (this.checkIfCurrentFormIsEmpty()) {
        this.displayToastNotification({
          type: 'warning',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: 'Please complete the ' + this.languageLabel(this.currentLanguage) + ' form'
        })
        return
      }
      this.setPageLoading(true)
      this.saving = true
      this.errorCounts.details = 0
      this.errorCounts.description = 0
      this.errorCounts.media = 0

      this.updateProperty()
        .then(response => {
          this.saving = false
          this.errorBag.clear()
          this.initVideo()
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check-circle',
            title: 'Saved',
            info: 'Your changes are now live'
          })
          this.initialLoadedProperty = JSON.parse(JSON.stringify(this.currentProperty))
          this.setPageLoading(false)
        }, error => {
          this.saving = false
          this.setPageLoading(false)
          if (error.response.status === 422) {
            this.errorBag.store(error.response.data)
            this.moreSpecsOpen = true
            this.displayError('Validation Errors', 'Your changes have not been saved. Please resolve the validation errors and try again.')

            for (let key in error.response.data.errors) {
              if (_.startsWith(key, 'details.') || _.startsWith(key, 'price_and_availability.') || _.startsWith(key, 'property_specs') || _.startsWith(key, 'open_house') || _.startsWith(key, 'agents')) {
                this.errorCounts.details++
              }
              if (_.startsWith(key, 'description.')) {
                this.errorCounts.description++
              }
              if (_.startsWith(key, 'location.') || _.startsWith(key, 'media.')) {
                this.errorCounts.media++
              }
            }

            return
          }

          this.displayError('Internal Server Error', 'Whoops! Looks like something went wrong.')
        })
    },
    deleteProperty () {
      this.showDeleteModal = false
      this.saving = true
      api.delete(`/properties/${this.currentProperty.id}`).then((response) => {
        this.saving = false
        this.currentProperty.deleted = response.data.deleted
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Deleted',
          info: 'This property is now marked as deleted.'
        })
      }, () => {
        this.saving = false
        this.displayError('Internal Server Error', 'Whoops! Looks like something went wrong.')
      })
    },
    restoreProperty () {
      this.saving = true
      api.post(`/properties/${this.currentProperty.id}/restore`).then((response) => {
        this.saving = false
        this.currentProperty.deleted = response.data.deleted
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Restored',
          info: 'This property is no longer marked as deleted.'
        })
      }, () => {
        this.saving = false
        this.displayError('Internal Server Error', 'Whoops! Looks like something went wrong.')
      })
    },
    duplicateProperty () {
      this.saving = true
      api.post(`/properties/duplicate/${this.currentProperty.id}`)
        .then((response) => {
          this.saving = false
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check-circle',
            title: 'Property Duplicated',
            info: 'This property has been duplicated successfully, we\'ve automatically redirected you to the duplicated property'
          })
          this.$router.push({ path: `/property-management/${response.data.property_id}` })
          this.currentProperty.id = response.data.property_id
          this.loadData()
        }, () => {
          this.saving = false
          this.displayError('Internal Server Error', 'Whoops! Something went wrong.')
        })
    },
    loadData () {
      this.initialOfficeId = this.selectedOffice.id ? this.selectedOffice.id : null
      this.setPageLoading(true)
      let property = this.fetchProperty({ propertyId: this.$route.params.property, type: 'cms' })
      let propertyEnums = this.fetchPropertyEnums()
      let agents = this.fetchPropertyAgents(this.$route.params.property).then((response) => {
        this.agents = response
      })

      Promise.all([property, propertyEnums, agents]).then(() => {
        // Load initial South Africa address dropdowns
        if (this.currentProperty.details.address_country === 'ZA') {
          let province = this.currentProperty.details.address_line4
          let city = this.currentProperty.details.address_town
          let suburb = this.currentProperty.details.address_line3

          // No province. Likely a new property. Load normally.
          if (!province) {
            this.countryChanged()
            this.initForm()
            return
          }

          // Pre-select province
          this.fetchProperty24Provinces('ZA').then(() => {
            for (let i = 0; i < this.property24.provinces.length; i++) {
              if (this.property24.provinces[i].name === province) {
                this.currentProvince = this.property24.provinces[i]
                break
              }
            }

            if (!this.currentProvince) {
              this.currentCity = {}
              this.currentSuburb = {}
              this.currentProvince = {}
              this.displayError('Error', 'Invalid province. Please reselect.')
              this.initForm()
              return
            }

            // Pre-select city
            this.fetchProperty24Cities(this.currentProvince.id).then(() => {
              for (let i = 0; i < this.property24.cities[this.currentProvince.id].length; i++) {
                if (this.property24.cities[this.currentProvince.id][i].name === city) {
                  this.currentCity = this.property24.cities[this.currentProvince.id][i]
                  break
                }
              }

              if (!this.currentCity) {
                this.currentCity = {}
                this.currentSuburb = {}
                this.displayError('Error', 'Invalid city. Please reselect.')
                this.initForm()
                return
              }

              // Pre-select suburb
              this.fetchProperty24Suburbs(this.currentCity.id).then(() => {
                for (let i = 0; i < this.property24.suburbs[this.currentCity.id].length; i++) {
                  if (this.property24.suburbs[this.currentCity.id][i].name === suburb) {
                    this.currentSuburb = this.property24.suburbs[this.currentCity.id][i]
                    break
                  }
                }

                if (!this.currentSuburb) {
                  this.currentSuburb = {}
                  this.displayError('Error', 'Invalid suburb. Please reselect.')
                  this.initForm()
                  return
                }

                // Now init form
                this.initForm()
              }, () => {
                this.displayError('Error', 'Failed to retrieve suburb listing from Property24')
                this.initForm()
              })
            }, () => {
              this.displayError('Error', 'Failed to retrieve city listing from Property24')
              this.initForm()
            })
          }, () => {
            this.displayError('Error', 'Failed to retrieve province listing from Property24')
            this.initForm()
          })
        } else {
          this.initForm()
        }
      })
    }
  },
  created () {
    this.loadData()
  },
  beforeDestroy: function () {
    clearInterval(this.interval)
  }
}
</script>

<style lang="scss" scoped>
.icon-img {
  background-size: cover;
  background-position: center;
}
.mh-30 {
  max-height: 25px;
}
ul.list-none {
  list-style-type: none;
  padding-left: 25px;
  li {
    margin-bottom: 2rem;
    .fas {
      margin-left: -25px;
    }
  }
}
</style>

<style lang="scss">
.editor.property-cms-editor {
  .ProseMirror {
    min-height: 12rem;
  }
}
</style>
